import React, { useState } from 'react';
import * as yup from 'yup';
import { Flex, Input, Link, Stack, Text } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'react-i18next';
import { useColorModeValue as mode } from '@/components/ui/color-mode';
import { Field } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useAuthDispatch } from '../../contexts/AuthContext';

type LoginFormProps = {
  callback(err: string | null, user?: { username: string; password: string }): void;
};

type FormData = {
  email?: string;
  password?: string;
};

const SignInForm = ({ callback }: LoginFormProps) => {
  const { logEvent } = useAnalytics();
  const { signIn, dispatch } = useAuthDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [checkingSSO, setCheckingSSO] = useState(false);
  const { t } = useTranslation('auth');

  const loginSchema = yup.object({
    email: yup.string().email(t('validation.validEmail')).required(t('validation.emailRequired')),
    password: showPassword ? yup.string().required(t('validation.passwordRequired')) : yup.string().optional()
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    trigger
  } = useForm<FormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const watchedEmail = watch('email');

  const initiateSSOLogin = async (provider: string) => {
    try {
      // const redirectUri = process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGN_IN || 'http://localhost:3000/auth/callback/';

      await signInWithRedirect({
        provider: {
          custom: provider
        }
        // customState: redirectUri
      });
    } catch (err: any) {
      console.error('Failed to initiate SSO login:', err);
      // Fallback to password login on SSO error
      setShowPassword(true);
    }
  };

  const checkForSSO = async (email: string) => {
    try {
      setCheckingSSO(true);
      const domain = email.split('@')[1];
      const response = await fetch(`${process.env.NEXT_PUBLIC_SSO_DOMAIN_LOOKUP_URL}?domain=${domain}`);
      const data = await response.json();

      if (data && data.providerName) {
        // SSO provider found, initiate SSO login
        await initiateSSOLogin(data.providerName);
        return true;
      }

      // No SSO provider, show password field
      return false;
    } catch (error) {
      console.error('Error checking SSO:', error);
      // On error, fallback to password login
      return false;
    } finally {
      setCheckingSSO(false);
    }
  };

  const onEmailContinue = async () => {
    const isValid = await trigger('email');

    if (!isValid) return;

    const hasSSO = await checkForSSO(watchedEmail);

    if (!hasSSO) {
      setShowPassword(true);
    }
  };

  const onSubmit = async (evt: any | undefined) => {
    if (!showPassword) {
      await onEmailContinue();
      return;
    }

    const { email, password } = evt;
    const { isSignedIn, nextStep } = await signIn({ email, password });

    // Forced password reset on new account login
    if (!isSignedIn && nextStep?.signInStep) {
      callback(nextStep?.signInStep, { username: email, password });
      return;
    }

    logEvent({
      action: 'signed_in',
      category: 'Sign in'
    });
  };

  const onForgotPassword = () => {
    dispatch({ type: 'resetPassword' });
  };

  const onBackToEmail = () => {
    setShowPassword(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        {/* Email */}
        <Field label={t('signIn.email')} required invalid={!!errors.email} errorText={errors.email?.message}>
          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                required
                id='email'
                name='email'
                type='email'
                autoComplete='email'
                placeholder={t('signIn.emailPlaceholder')}
                disabled={showPassword}
              />
            )}
          />
        </Field>

        {showPassword && (
          <Field label={t('signIn.password')} required invalid={!!errors.password} errorText={errors.password?.message}>
            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  required
                  id='password'
                  name='password'
                  type='password'
                  autoComplete='current-password'
                  placeholder={t('signIn.passwordPlaceholder')}
                />
              )}
            />
          </Field>
        )}
      </Stack>

      {showPassword && (
        <Flex align='center' justify='space-between' mt='8'>
          <Link colorPalette='brand' variant='underline' onClick={onBackToEmail}>
            {t('signIn.backToEmail')}
          </Link>
          <Link colorPalette='brand' variant='underline' onClick={onForgotPassword}>
            {t('signIn.forgotPassword')}
          </Link>
        </Flex>
      )}

      <Button
        type='submit'
        mt='8'
        w='full'
        loading={isSubmitting || checkingSSO}
        loadingText={checkingSSO ? t('signIn.checking') : t('signIn.signingIn')}
        disabled={Object.keys(errors).length > 0}
      >
        {showPassword ? t('signIn.submit') : t('signIn.continue')}
      </Button>
      <Text fontSize='xs' color={mode('gray.600', 'gray.400')} mt='8' textAlign='center'>
        <Trans
          i18nKey='signIn.termsAcknowledge'
          t={t}
          components={{
            terms: (
              <Link
                href='/legal/terms/'
                target='_blank'
                rel='noopener noreferrer'
                colorPalette='brand'
                variant='underline'
              />
            )
          }}
        />
      </Text>
    </form>
  );
};

export default SignInForm;
